import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Post } from '@/lib/posts'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { delay } from '@/lib/motion'
import { getPosts } from '@/lib/posts'
import { formatPostDate } from '@/lib/dates'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PostWindow } from '@/components/blog/post-cover'
import { Ambient } from '@/components/generals/backdrops'
import { Container } from '@/components/generals/container'
import { ClosingCta } from '@/components/generals/closing-cta'
import { PostGridCard, PostMeta, postHref } from '@/components/blog/post-card'
import { routes } from '@/constants/site'

interface BlogProps {
    posts: Post[]
}

interface FeaturedPostProps {
    post: Post
}

const LABEL = 'text-[11.5px] leading-4 text-ink/80'

/**
 * The featured story on a frayed card. Wide screens set the copy beside the art, with the label
 * and date at the top and the title at the bottom; below 1100px it takes the grid cards' anatomy.
 */
const FeaturedPost: FC<FeaturedPostProps> = ({ post }): ReactNode => {
    return (
        <article className='group enter relative isolate mt-12 p-2 max-[700px]:mt-8' style={delay(150)}>
            <span aria-hidden='true' className='fray absolute inset-0 -z-10 rounded-[2px] bg-card' />

            <div className='flex flex-col min-[1101px]:grid min-[1101px]:grid-cols-[minmax(0,456px)_minmax(0,768px)] min-[1101px]:justify-between min-[1101px]:gap-[clamp(24px,4vw,96px)]'>
                <div className='px-3 pb-3 min-[1101px]:flex min-[1101px]:min-h-[450px] min-[1101px]:flex-col min-[1101px]:justify-between min-[1101px]:py-8 min-[1101px]:pl-8 min-[1101px]:pr-6'>
                    <div className='hidden flex-col gap-[5px] min-[1101px]:flex'>
                        <p className={cn(LABEL, 'uppercase tracking-[0.06em]')}>Featured</p>
                        <p className='text-[11.5px] leading-4 text-ink/60'>
                            <time dateTime={post.publishedAt}>{formatPostDate(post.publishedAt)}</time>
                        </p>
                    </div>

                    <PostMeta post={post} className='mt-5 min-[1101px]:hidden' />

                    <div className='flex flex-col min-[1101px]:gap-[11px]'>
                        <p className={cn(LABEL, 'hidden tracking-[-0.01em] min-[1101px]:block')}>{post.category}</p>
                        <h2 className='mt-[13px] font-serif text-[26.7px] leading-[34px] tracking-[-0.024em] text-ink transition-opacity duration-150 group-hover:opacity-80 max-[700px]:text-[24px] max-[700px]:leading-[31px] min-[1101px]:mt-4 min-[1101px]:text-[clamp(28px,2.5vw,36px)] min-[1101px]:leading-[1.22] min-[1101px]:tracking-[-0.03em]'>
                            <Link href={postHref(post.slug)} className='after:absolute after:inset-0 after:z-10 after:content-[""] focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-4 focus-visible:after:outline-ink/75'>
                                {post.title}
                            </Link>
                        </h2>
                        <p className='mt-[13px] text-[16.5px] leading-[25px] tracking-[-0.01em] text-ink/[0.68] min-[1101px]:mt-0 min-[1101px]:text-balance min-[1101px]:text-[16px] min-[1101px]:text-ink/80'>
                            {post.excerpt}
                        </p>
                    </div>
                </div>

                <PostWindow post={post} priority className='order-first aspect-[752/454] min-[1101px]:order-none min-[1101px]:aspect-auto min-[1101px]:min-h-full' />
            </div>
        </article>
    )
}

const Blog: FC<BlogProps> = ({ posts }): ReactNode => {
    const featured = posts.find(post => post.featured) ?? posts[0]

    return (
        <Layout>
            <Seo
                title='Blog'
                description='The stories behind the designers: essays from Kerning scouts and the team on finding, judging and hiring designers who sweat the details.'
            />

            <section id='blog' aria-labelledby='blog-title' className='pt-hero hero-field relative isolate overflow-hidden pb-[88px] max-[700px]:pb-14'>
                <Ambient className='-z-10' />

                <Container size='wide' className='relative'>
                    <h1 id='blog-title' className='type-hero enter mx-auto max-w-[18ch] text-center'>
                        The stories behind the designers
                    </h1>

                    {featured ? (
                        <FeaturedPost post={featured} />
                    ) : (
                        <div className='enter mx-auto mt-12 max-w-[46ch] text-center' style={delay(150)}>
                            <p className='text-[16px] leading-[25px] text-ink/70'>
                                New stories are on their way. Our first essays are being edited. In the meantime, see how a scout-led search works.
                            </p>
                            <Link href={routes.howItWorks} className='mt-5 inline-block text-ink underline decoration-ink/30 underline-offset-4'>
                                How it works
                            </Link>
                        </div>
                    )}
                </Container>
            </section>

            {posts.length > 0 && (
                <section id='all-articles' aria-labelledby='all-articles-title' className='pb-24 pt-[clamp(40px,4vw,56px)]'>
                    <Container size='wide'>
                        <h2 id='all-articles-title' className='reveal mb-8 font-serif text-[28px] leading-[38px] tracking-[-0.03em] text-ink'>
                            All articles
                        </h2>

                        <div className='reveal grid grid-cols-3 gap-10 p-2 max-[1100px]:grid-cols-2 max-[700px]:grid-cols-1 max-[700px]:gap-6'>
                            {posts.map(post => (
                                <PostGridCard key={post.id} post={post} />
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            <ClosingCta
                id='blog-cta'
                line="Tell us who you're looking for and a scout who knows the work will come back with one name"
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<BlogProps> = async () => {
    try {
        const posts = await getPosts()

        return { props: { posts }, revalidate: 60 }
    }
    catch (error) {
        console.error('Could not load blog posts:', error instanceof Error ? error.message : error)

        return { props: { posts: [] }, revalidate: 30 }
    }
}

export default Blog
